import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Tenant } from '../../../types/tenants';
import { SubscriptionChangePreview, SubscriptionTariff } from '../../../types/subscriptions';
import { useSubscriptionTariffsRequests } from '../../../api/subscription_tariffs';
import {
  useChangeTenantSubscription,
  useEnterpriseInquiry,
  useSubscriptionChangePreview,
} from '../../../api/subscriptions';
import { useAuthContext } from '../../../contexts/auth';
import CustomModal from '../../../components/ui/modal/modal.component';
import { DatePickerLocalizationProvider } from '../../../components/ui/useDatePickerLocalization';
import { DATE_TIME_PICKER_FORMAT } from '../../../components/ui/datePickerFormats';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../../consts';
import { formatNumber, formatQuantity } from '../../../utils';
import styles from './changeSubscription.module.css';

interface ChangeSubscriptionProps {
  tenant?: Tenant;
  onSuccess?: () => void;
  variant?: 'default' | 'gate';
}

const sameId = (a: string | number | undefined, b: string | number | undefined) =>
  a !== undefined && b !== undefined && String(a) === String(b);

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isValidPhone = (value: string) => {
  if (!/^\+?[\d\s().-]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
};
const isValidContact = (value: string) => {
  const trimmed = value.trim();
  return isValidEmail(trimmed) || isValidPhone(trimmed);
};

export const ChangeSubscription: React.FC<ChangeSubscriptionProps> = ({
  tenant,
  onSuccess,
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  const { loading: tariffsLoading, error: tariffsError, query } = useSubscriptionTariffsRequests();
  const { loading: changeLoading, error: changeError, change } = useChangeTenantSubscription();
  const { preview: fetchPreview } = useSubscriptionChangePreview();
  const { loading: inquiryLoading, submit: submitInquiry } = useEnterpriseInquiry();
  const [tariffs, setTariffs] = useState<SubscriptionTariff[]>([]);
  const [selectedTariffId, setSelectedTariffId] = useState<string>('');
  const [preview, setPreview] = useState<SubscriptionChangePreview | null>(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryContact, setInquiryContact] = useState('');
  const [inquiryWhen, setInquiryWhen] = useState<Dayjs | null>(null);

  const subscription = currentUser?.tenant?.subscription ?? tenant?.subscription;
  // A gated (TRIAL_EXPIRED) subscription is still status 'T', but the trial is
  // over - plan changes then raise a real invoice, so the "free" notice is wrong.
  const isTrial = subscription?.status === 'T' && !subscription?.is_blocked;
  const isGate = variant === 'gate';
  // On the lapsed-subscription gate (trial ended, or dunning exhausted),
  // re-selecting the current plan is a valid "restore my subscription" action,
  // so it must stay selectable. An overage block is different - the current
  // plan genuinely can't cover the device count, so keep it disabled there.
  const canRepickCurrentPlan = isGate && subscription?.block_reason !== 'OVERAGE';
  const measuredDevices = subscription?.measured_device_count ?? 0;

  useEffect(() => {
    const loadTariffs = async () => {
      const response = await query();
      if (response?.results) {
        setTariffs(response.results);
      }
    };
    loadTariffs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentTariffId = subscription?.tariff?.id ?? '';

  useEffect(() => {
    setSelectedTariffId(String(currentTariffId));
  }, [currentTariffId]);

  const runPreview = useCallback(
    async (tariffId: string, isCustomPriced: boolean) => {
      if (!tariffId || sameId(tariffId, currentTariffId) || isCustomPriced) {
        setPreview(null);
        return;
      }
      setPreview(await fetchPreview(tariffId));
    },
    [currentTariffId, fetchPreview]
  );

  const handleTariffChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.target.value;
    const tariff = tariffs.find((row) => sameId(row.id, id));
    if (tariff?.is_custom_priced) {
      setInquiryOpen(true);
      setPreview(null);
      return;
    }
    setSelectedTariffId(id);
    runPreview(id, false);
  };

  const handleChangeSubscription = async () => {
    if (!selectedTariffId || changeLoading) {
      return;
    }
    const { success, data } = await change({ tariff: selectedTariffId });
    if (!success) {
      enqueueSnackbar(t('Change subscription failed'), ERROR_SNACKBAR);
      return;
    }

    // Recovering a lapsed subscription: the plan change raised an invoice. With
    // a card on file it is charged automatically and the provider webhook
    // restores access; with no card the gate now shows the card form
    // (needs_card). Either way just refresh and let the gate re-render.
    if (isGate && data?.is_blocked) {
      window.dispatchEvent(new Event('tenantUpdated'));
      if (data.needs_card) {
        enqueueSnackbar(t('Add a payment card to finish restoring access.'), ERROR_SNACKBAR);
      } else {
        onSuccess?.();
      }
      return;
    }

    window.dispatchEvent(new Event('tenantUpdated'));
    enqueueSnackbar(t('Change subscription successfully'), SUCCESS_SNACKBAR);
    onSuccess?.();
  };

  const closeInquiry = () => {
    setInquiryOpen(false);
    setInquiryContact('');
    setInquiryWhen(null);
  };

  const contactValid = isValidContact(inquiryContact);
  const whenValid = !inquiryWhen || inquiryWhen.isValid();

  const handleInquirySubmit = async () => {
    if (!contactValid || !whenValid || inquiryLoading) {
      return;
    }
    const { success } = await submitInquiry({
      contact: inquiryContact.trim(),
      ...(inquiryWhen && inquiryWhen.isValid()
        ? { preferred_contact_at: inquiryWhen.toISOString() }
        : {}),
    });
    if (success) {
      enqueueSnackbar(t('Request sent. We will contact you soon.'), SUCCESS_SNACKBAR);
      closeInquiry();
      onSuccess?.();
    } else {
      enqueueSnackbar(t('Change subscription failed'), ERROR_SNACKBAR);
    }
  };

  if (tariffsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const visibleTariffs =
    variant === 'gate' && subscription?.block_reason === 'OVERAGE'
      ? tariffs.filter(
          (tariff) => tariff.equipment_limit === null || tariff.equipment_limit >= measuredDevices
        )
      : tariffs;

  const vatPercentRaw = tariffs.find((tariff) => tariff.vat_percent)?.vat_percent;
  const vatPercent = vatPercentRaw ? formatQuantity(vatPercentRaw) : null;

  return (
    <Box className={styles.container}>
      <Typography component="p" variant="subtitle1" className={styles.radioLabel} color="text.primary">
        {t('Available subscription plans')}
      </Typography>

      {isTrial && <Alert severity="info">{t('During the trial, plan changes are free.')}</Alert>}

      {subscription?.pending_tariff && (
        <Alert severity="info">
          {t('Downgrade to {{plan}} takes effect on {{date}}.', {
            plan: subscription.pending_tariff.name,
            date: subscription.pending_tariff_effective ?? '',
          })}
        </Alert>
      )}

      <RadioGroup
        aria-label={t('Subscription plans')}
        name="subscription-tariff"
        value={selectedTariffId}
        onChange={handleTariffChange}
        className={styles.radioGroup}
      >
        {visibleTariffs.map((tariff) => (
          <FormControlLabel
            key={tariff.id}
            value={String(tariff.id)}
            control={<Radio size="small" />}
            disabled={!canRepickCurrentPlan && sameId(tariff.id, currentTariffId)}
            sx={{ ml: 0, alignItems: 'flex-start' }}
            label={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
                <Typography variant="body1">{tariff.name}</Typography>
                {tariff.is_custom_priced ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('Individual pricing per active device')}
                    <Tooltip title={t('We will contact you to discuss pricing.')}>
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary', cursor: 'help', ml: 0.5, verticalAlign: 'middle' }}
                      />
                    </Tooltip>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('Net price')}: {formatNumber(tariff.price_net)} {t('/ month')}
                  </Typography>
                )}
                {tariff.equipment_limit !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {t('Equipment limit')}: {tariff.equipment_limit}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </RadioGroup>

      {vatPercent && (
        <Typography variant="caption" color="text.secondary">
          {t('Prices shown are net. {{rate}}% VAT will be added.', { rate: vatPercent })}
        </Typography>
      )}

      {preview && preview.direction === 'upgrade' && (
        <Alert severity="info">
          {t('Upgrade takes effect immediately.')} {t('Prorated charge')}:{' '}
          {formatNumber(preview.proration_net)} {t('net')}. {t('Next month')}:{' '}
          {formatNumber(preview.next_month_net)}{' '}
          {t('net')}.
        </Alert>
      )}
      {preview && preview.direction === 'downgrade' && preview.effective_date && (
        <Alert severity="info">
          {t('Downgrade to {{plan}} takes effect on {{date}}.', {
            plan: tariffs.find((tariff) => sameId(tariff.id, selectedTariffId))?.name ?? '',
            date: preview.effective_date,
          })}
        </Alert>
      )}

      {tariffsError?.detail && <div className={styles.errorMessage}>{tariffsError.detail}</div>}
      {changeError?.tariff?.[0]?.message && (
        <div className={styles.errorMessage}>{changeError.tariff[0].message}</div>
      )}
      {typeof changeError?.tariff === 'string' && (
        <div className={styles.errorMessage}>{changeError.tariff}</div>
      )}

      <Button
        variant="contained"
        color="success"
        onClick={handleChangeSubscription}
        disabled={
          !selectedTariffId ||
          changeLoading ||
          (!canRepickCurrentPlan && sameId(selectedTariffId, currentTariffId))
        }
      >
        {changeLoading
          ? t('Saving...')
          : isGate
            ? t('Restore access')
            : t('Update subscription')}
      </Button>

      <CustomModal
        opened={inquiryOpen}
        setOpened={(value) => (value ? setInquiryOpen(true) : closeInquiry())}
        title={t('Enterprise plan')}
        width="min(440px, 92vw)"
        minWidth="min(440px, 92vw)"
        content={
          <Box className={styles.inquiryForm}>
            <Typography variant="body2" color="text.secondary">
              {t(
                'Leave a contact — we will reach out to discuss the details of the Enterprise plan.'
              )}
            </Typography>
            <TextField
              label={t('Phone or email for contact')}
              size="small"
              value={inquiryContact}
              onChange={(event) => setInquiryContact(event.target.value)}
              error={inquiryContact.trim().length > 0 && !contactValid}
              helperText={
                inquiryContact.trim().length > 0 && !contactValid
                  ? t('Enter a valid email address or phone number.')
                  : undefined
              }
              fullWidth
            />
            <DatePickerLocalizationProvider>
              <DateTimePicker
                label={t('Preferred contact date and time (optional)')}
                value={inquiryWhen}
                onChange={(value: Dayjs | null) => setInquiryWhen(value)}
                disablePast
                ampm={false}
                format={DATE_TIME_PICKER_FORMAT}
                minutesStep={15}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                  actionBar: { actions: ['clear', 'accept'] },
                }}
              />
            </DatePickerLocalizationProvider>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={closeInquiry}>{t('Cancel')}</Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleInquirySubmit}
                disabled={!contactValid || !whenValid || inquiryLoading}
              >
                {inquiryLoading ? t('Saving...') : t('Send request')}
              </Button>
            </Box>
          </Box>
        }
      />
    </Box>
  );
};
